import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { chatFetch, extractErrorMessage } from '../../Services/authClient';

const RivalryPage = () => {
  const { t } = useTranslation();
  const [challenges, setChallenges] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [status, setStatus] = useState('loading');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    chatFetch('/chat-api/v2/categories/')
      .then((payload) => {
        const list = Array.isArray(payload) ? payload : payload?.results || [];
        setCategories(list);
      })
      .catch(() => setCategories([]));
  }, []);

  const loadChallenges = useCallback(() => {
    setStatus('loading');
    chatFetch('/chat-api/v2/challenges/')
      .then((payload) => {
        const list = Array.isArray(payload) ? payload : payload?.results || [];
        setChallenges(list);
        setStatus(list.length === 0 ? 'empty' : 'ready');
      })
      .catch((error) => setStatus(extractErrorMessage(error, t('app.rivalry.loadError', 'Unable to load rivalries.'))));
  }, [t]);

  useEffect(() => {
    loadChallenges();
  }, [loadChallenges]);

  const filteredChallenges = useMemo(() => {
    return challenges.filter((challenge) => {
      const matchesCategory = selectedCategory === 'all' || challenge.category === selectedCategory || challenge.category_slug === selectedCategory;
      const haystack = `${challenge.home_team || ''} ${challenge.away_team || ''}`.toLowerCase();
      const matchesSearch = haystack.includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [challenges, selectedCategory, searchTerm]);

  return (
    <div className="rivalry">
      <div className="rivalry__filters">
        <input
          type="search"
          placeholder={t('app.rivalry.search', 'Search rivalries, teams…')}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        <select value={selectedCategory} onChange={(event) => setSelectedCategory(event.target.value)}>
          <option value="all">{t('app.rivalry.categoryAll', 'All categories')}</option>
          {categories.map((category) => (
            <option key={category.slug || category.id} value={category.slug || category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {status === 'loading' && <p className="rivalry__status">{t('app.rivalry.loading', 'Loading rivalries…')}</p>}
      {status !== 'loading' && filteredChallenges.length === 0 && (
        <p className="rivalry__status">{t('app.rivalry.empty', 'No rivalries meet that filter yet.')}</p>
      )}

      <div className="rivalry__grid">
        {filteredChallenges.map((challenge) => (
          <article key={challenge.id} className="rivalry-card">
            <header>
              <span className="rivalry-card__tag">{challenge.category || t('app.rivalry.defaultCategory', 'College Football')}</span>
              <small>{t('app.rivalry.waiting', 'Waiting to start')}</small>
            </header>
            <div className="rivalry-card__teams">
              <div>
                <p>{challenge.home_team}</p>
                <span>{t('app.rivalry.requests', 'Requests needed')}</span>
                <strong>{challenge.requests_needed || challenge.requests || 0}</strong>
              </div>
              <div className="rivalry-card__vs">vs</div>
              <div>
                <p>{challenge.away_team}</p>
                <span>{t('app.rivalry.statusLabel', 'Status')}</span>
                <strong>{challenge.status || t('app.rivalry.pending', 'Pending')}</strong>
              </div>
            </div>
            <footer>
              <button type="button" className="rivalry-card__primary">{t('app.rivalry.request', 'Request to start')}</button>
              <button type="button" className="rivalry-card__ghost">{t('app.rivalry.share', 'Share')}</button>
            </footer>
          </article>
        ))}
      </div>
    </div>
  );
};

export default RivalryPage;
